import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Input,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getRegions } from '../../actions/adminActions';
import {
  addFlightTestPlan,
  getLandingPadSlotAvailability
} from '../../actions/flightTestPlanActions';
import { getLandingPads } from '../../actions/liveAircraftActions';
import { getCells } from '../../actions/orderActions';
import { tsToLocalDateString } from '../../utils/date-utils';

const CreateFlightTestPlan = ({
  cells,
  selectedCellId,
  activePads,
  landingPadSlots,
  getCells,
  getRegions,
  getLandingPads,
  addFlightTestPlan,
  getLandingPadSlotAvailability
}) => {
  const navigate = useNavigate();
  const [generatedCellId, setGeneratedCellId] = useState(selectedCellId);
  const [cellId, setCellId] = useState(3);
  const [endTime, setEndTime] = useState(new Date().toISOString());
  const [testNotes, setTestNotes] = useState('');
  const [simulateVendor, setSimulateVendor] = useState(true);
  const [simulateMc, setSimulateMc] = useState(true);
  const [randomPos, setRandomPos] = useState(false);
  const [selectedLandingPadIds, setSelectedLandingPadIds] = useState([]);
  const [totalFreeSlotsSelected, setTotalFreeSlotsSelected] = useState(0);

  const getAvailabilityForAllSlots = (landingPads, cid) => {
    landingPads.forEach(p => {
      getLandingPadSlotAvailability(cid, p.id);
    });
  };

  useEffect(() => {
    getLandingPads();
    getRegions();
    getCells();
    getAvailabilityForAllSlots(
      activePads.filter(p => p.cellId === cellId),
      cellId
    );
  }, []);

  const getLandingPadsForCell = (cellId: number) => {
    return activePads.filter(p => p.cellId === cellId);
  };

  const updateTotalFreeSlotsSelected = (): void => {
    let total = 0;
    selectedLandingPadIds.forEach(lp => {
      const availability = (landingPadSlots[lp] || []).length;
      total = total + availability;
    });
    setTotalFreeSlotsSelected(total);
  };

  const getCellMenuItems = (filterByType: 'PROD' | 'TEST'): JSX.Element => {
    return cells
      ?.filter(c => c.cellType === filterByType)
      .map(c => {
        return (
          <MenuItem key={`cell-${c.id}-${filterByType}`} value={c.id}>
            <Box
              sx={{}}
              alignItems="center"
              display={'flex'}
              flexGrow={1}
              justifyContent="center">
              <Box display={'flex'}>
                <Typography variant="h6">{c.cellName}</Typography>
              </Box>
            </Box>
          </MenuItem>
        );
      });
  };

  return (
    <div style={{ backgroundColor: 'white', height: '100%', padding: '12px 6px' }}>
      <div style={{ padding: 6 }}>
        <Typography>Test Cell</Typography>
        <Select
          value={cellId}
          onChange={e => {
            const newCell = Number(e.target.value);
            setCellId(newCell);
            setSelectedLandingPadIds([]);
            getAvailabilityForAllSlots(
              activePads.filter(p => p.cellId === newCell),
              newCell
            );
          }}>
          {getCellMenuItems('TEST')}
        </Select>
      </div>
      <div style={{ padding: 6 }}>
        <Typography>Generated Cell</Typography>
        <Select
          value={generatedCellId}
          onChange={e => {
            setGeneratedCellId(Number(e.target.value));
          }}>
          {getCellMenuItems('PROD')}
        </Select>
      </div>
      <div style={{ padding: 6 }}>
        <Typography>End time</Typography>
        <TextField
          id="datetime-local"
          type="datetime-local"
          value={tsToLocalDateString(endTime, 'utc')}
          onChange={e => setEndTime(e.target.value)}
          onBlur={e => setEndTime(e.target.value)}
          style={{
            marginLeft: 0,
            marginRight: 0,
            marginBottom: 10,
            fontSize: 14,
            width: 200
          }}
          inputProps={{
            style: {
              fontSize: 14,
              fontWeight: 500,
              padding: '3px 0px'
            }
          }}
          InputLabelProps={{
            shrink: true
          }}
        />
      </div>
      <div style={{ padding: 6 }}>
        <Typography>Simulation notes</Typography>
        <Input
          style={{ marginLeft: 6 }}
          value={testNotes}
          onChange={e => setTestNotes(e.target.value)}
          placeholder="Add a note"
        />
      </div>
      <div style={{ padding: 6 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={randomPos}
              onChange={e => setRandomPos(!randomPos)}
              value={randomPos}
            />
          }
          label="Random store"
        />
      </div>
      <div style={{ padding: 6 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={simulateVendor}
              onChange={e => setSimulateVendor(!simulateVendor)}
              value={simulateVendor}
            />
          }
          label="Simulate Vendor"
        />
      </div>
      <div style={{ padding: 6 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={simulateMc}
              onChange={e => setSimulateMc(!simulateMc)}
              value={simulateMc}
            />
          }
          label="Simulate MC"
        />
      </div>
      <div style={{ padding: 6 }}>
        <Typography>Landing pads</Typography>
        {getLandingPadsForCell(cellId).map(p => {
          return (
            <div key={`landing-pad-${p.id}`}>
              <FormControlLabel
                control={
                  <Checkbox
                    value={selectedLandingPadIds.includes(p.id)}
                    onChange={() => {
                      const pads = selectedLandingPadIds;
                      if (pads.includes(p.id)) {
                        const index = pads.indexOf(p.id);
                        pads.splice(index, 1); // 2nd parameter means remove one item only
                      } else {
                        pads.push(p.id);
                      }
                      setSelectedLandingPadIds(pads);
                      updateTotalFreeSlotsSelected();
                    }}
                  />
                }
                label={
                  landingPadSlots[p.id]
                    ? `Landing pad ${p.name} (${
                        landingPadSlots[p.id].length
                      } slots)`
                    : `Landing pad ${p.name}`
                }
              />
            </div>
          );
        })}
      </div>
      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          addFlightTestPlan({
            cellId,
            generatedCellId,
            targetEndDate: new Date(endTime).toISOString(),
            note: testNotes,
            simulateVendor,
            simulateMc,
            landingPads: selectedLandingPadIds.map(i =>
              activePads.find(p => p.id === i)
            ),
            randomPos
          });

          navigate('../', { replace: true });
        }}>
        {`Begin simulation (${totalFreeSlotsSelected} slots)`}
      </Button>
    </div>
  );
};
const mapStateToProps = state => ({
  cells: state.orders.cells,
  selectedCellId: state.orders.selectedCellId,
  activePads: state.liveAircraft.activePads,
  landingPadSlots: state.flightTestPlans.landingPadSlots
});

const mapDispatchToProps = dispatch => ({
  getCells: () => dispatch(getCells()),
  getRegions: () => dispatch(getRegions()),
  getLandingPads: () => dispatch(getLandingPads()),
  addFlightTestPlan: testPlan => dispatch(addFlightTestPlan(testPlan)),
  getLandingPadSlotAvailability: (cellId: number, landingPadId: number) =>
    dispatch(getLandingPadSlotAvailability(cellId, landingPadId))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CreateFlightTestPlan);
